import { hasAnyPermission, type PermissionCode, type SessionSnapshot } from '@/auth/types';
import { ZONE_PATHS } from '@/auth/zone-home';
import { listTabHref } from '@/lib/task-list-url';

/**
 * TEKLIF LISTESI SEKMELERI -> SUNUCU FILTRESI (buro Tekliflerim).
 *
 * Bilesen DISINDA: saf fonksiyon olarak sinaniyor (__tests__/tab-params.test.ts). Musterinin
 * "Gelen teklifler" eslemesi (customerOfferTabParams) ekranla birlikte 2026-10-02'de kaldirildi.
 */

export const OFFER_TABS = ['openOffers', 'paymentWaitingOffers', 'passedOffers'] as const;
export type OfferTab = (typeof OFFER_TABS)[number];

/** Tekliflerim'in acilis sekmesi (Bekleyenler). */
export const DEFAULT_OFFER_TAB: OfferTab = 'openOffers';

/** Tekliflerim'in bir sekmesinin adresi (menus.ts `exchange.offers`). */
export const exchangeOffersHref = (tab: OfferTab): string =>
  listTabHref(`${ZONE_PATHS.exchange}/teklifler`, tab, DEFAULT_OFFER_TAB);

/**
 * Tekliflerim -> Bekleyenler. Yeni kazanilan isin ilk adimi ("Odeme alindi") burada; kabuktaki
 * kazanma bildirimi (2026-10-02) buraya goturur.
 */
export const pendingOffersHref = (): string => exchangeOffersHref('openOffers');

/**
 * KAZANILAN is Tekliflerim'in hangi sekmesinde? Asagidaki eslemenin aynasi: 'pending'
 * Bekleyenler'de, 'payment_received' Odenecekler'de, gerisi (tamamlandi, uyari, cozuldu...)
 * Gecmis'te. Piyasa detayindaki "Kazandiniz" karti talep hangi durumdaysa oraya goturur;
 * Piyasa Gecmis'inden gunler sonra acilan tamamlanmis is Bekleyenler'de bulunmaz.
 */
export const wonOfferTabFor = (taskStatusCode: string | null): OfferTab => {
  if (taskStatusCode === 'pending') return 'openOffers';
  if (taskStatusCode === 'payment_received') return 'paymentWaitingOffers';
  return 'passedOffers';
};

export type ExchangeOfferScope = {
  /** Oturumdaki kullanici (sunucu `offerUserId`yi yine cagirana zorlar). */
  userId: string;
  /**
   * ORG KAPSAMI: kimde acik oldugu `hasOrganizationOfferScope`'ta (asagida). Kabuktaki kazanma
   * bildirimi (win-notice.ts) bilerek KENDI kapsamiyla (false) okur.
   */
  organizationScope: boolean;
};

/** Tekliflerim'de ORG kapsamini acan yetkiler (sunucu offerOrganizationScopeOf ile ayni liste). */
export const ORGANIZATION_OFFER_SCOPE_PERMISSIONS: readonly PermissionCode[] = [
  'createOffer',
  'editOffer',
  'completeTask',
];

/**
 * Tekliflerim ORG KAPSAMIYLA mi okunur? Cagiran bir organizasyonun uyesi VE `createOffer`,
 * `editOffer`, `completeTask` yetkilerinden EN AZ BIRI var (yonetici ortuk yetkili; kural
 * hasAnyPermission'da). Sunucu kapsami yalniz bu durumda aciyor (app-service
 * offerOrganizationScopeOf).
 *
 * 2026-10-03 (K2, bir buro bir teklif): eskiden yalniz `completeTask` — kazanilan is teklifi
 * veren personel olmadan da kapanabilsin diye. Artik buronun talep basina TEK teklifi var ve is
 * arkadasi onu guncelleyebiliyor; yalniz kendi kapsamiyla okuyan eleman guncelledigi teklifi
 * Tekliflerim'de goremezdi (sahip ilk veren kalir).
 *
 * Org'suz buroda kapsam hic acilmaz: hasPermission orada her koda evet der ama sunucu kendi
 * tekliflerini doner.
 */
export const hasOrganizationOfferScope = (
  snapshot: Pick<SessionSnapshot, 'organization' | 'memberType' | 'permissions'> | null,
): boolean =>
  Boolean(snapshot?.organization) &&
  hasAnyPermission(snapshot, ORGANIZATION_OFFER_SCOPE_PERMISSIONS);

/**
 * BURO — Bekleyenler / Odenecekler / Gecmis (2026-09-26, iki adimli onay).
 *
 *   Bekleyenler : `offerTab=waiting` — karar bekleyen KENDI tekliflerim + kazanilip musterinin
 *                 odemesini bekleyen ('pending') isler. taskStatus/passedTasks ile IFADE
 *                 EDILEMIYOR: karisik durum listesi sunucudaki "yalniz kazanan teklif" join'ini
 *                 kapatir (kaybedilen teklifler sizar). Bu yuzden ikisiyle BIRLESTIRILMEZ.
 *   Odenecekler : 'payment_received' — odeme alindi, gonderim + "Islemi tamamla" bekleniyor.
 *   Gecmis      : `offerTab=history` (2026-10-02, M1) — kapanmis isler + musterinin BASKA bir
 *                 buroyu sectigi isler. Eskiden kapali faz + kapali durumdu ve kaybedilen teklif,
 *                 is kapanana kadar (50 dk'ya kadar) hicbir sekmede gorunmuyordu.
 *
 * `offerUserId` org kapsaminda GONDERILMEZ: sunucu onu cagirana zorlayip listeyi YALNIZ kendi
 * tekliflerine daraltir ve org kapsami bosa gider. Gecmis kisiye ait kalir (org kapsami yalniz
 * aksiyon gereken sekmelerde).
 */
export const exchangeOfferTabParams = (
  tab: OfferTab,
  scope: ExchangeOfferScope,
): Record<string, string> => {
  const owner: Record<string, string> =
    scope.organizationScope && tab !== 'passedOffers'
      ? { offerScope: 'organization' }
      : { offerUserId: scope.userId };
  if (tab === 'openOffers') return { ...owner, offerTab: 'waiting' };
  if (tab === 'paymentWaitingOffers') return { ...owner, taskStatus: 'payment_received' };
  return { ...owner, offerTab: 'history' };
};
